import { prop } from 'ramda';
import { useMemo } from 'react';
import { useGetTwoFactorConf } from 'server';
import { useUserSelector } from 'js/store/hooks';
import { useShowLogin } from 'js/hooks/useShowLogin';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { getFitToPlaySubmissionRequired } from 'js/hooks/auth/pas';
import {
    useGetTemporaryAuthenticationToken,
    useAddCredentialsLoginHook,
    useAddBeforeRegistrationHook,
    useGetBeforeRegistrationHandler,
    useLoginByCredentials,
    useRegister,
    useLogout
} from 'js/hooks/auth/hooks';

export const useAuthPublicAPI = () => {
    const login = useLoginByCredentials();
    const logout = useLogout();
    const register = useRegister();
    const showLogin = useShowLogin();
    const showLoginPopup = useLoginPopup();
    const addAfterCredentialsLoginCallback = useAddCredentialsLoginHook();
    const addBeforeRegistrationHook = useAddBeforeRegistrationHook();
    const getBeforeRegistrationHandler = useGetBeforeRegistrationHandler();
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();
    const getTwoFactorConfiguration = useGetTwoFactorConf();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    return useMemo(() => ({
        /**
         * Create new player. see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/CreatePlayerInfo.html}
         * @memberOf Playtech.API.auth
         * @function
         * @param {Object} dataMap see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/CreatePlayerInfoDataMap.html}.
         * @param {Object} [addressDetails] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/AddressDetails.html}.
         * @param {Object} [contactAddress] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/ContactAddress.html}.
         * @param {Object} [depositLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/DepositLimit.html}.
         * @param {Object} [betLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/PlayerRgLimitChangeData.html}.
         * @param {string} [betLimitsAction] Values: 'logout', 'message'.
         * @param {Object} [lossLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/PlayerRgLimitChangeData.html}.
         * @param {string} [lossLimitsAction] Values: 'logout', 'message'.
         * @param {Object} [contactPreferences]
         * @param {string} [contactPreferences.channel] Values: 'email', 'phone', 'SMS', 'mobilePush', 'internalMessage', 'desktopCompanion', 'whatsApp', 'directMail'.
         * @param {string} [contactPreferences.type] Values: 'promotional', 'account', 'thirdParty'.
         * @param {boolean}[contactPreferences.preference]
         * @param {boolean} [contactPreferences.isInvalid] True if channel is marked as invalid.
         * @param {Object[]} [tags]
         * @param {string} [tags[].name]
         * @param {string} [tags[].value] Values: 'text', 'number', 'date', 'delta'
         * @param {string} [tags[].type]
         * @param {string} [tags[].comment]
         * @param {string} [tags[].expirationDate] Format: yyyy-MM-dd 2016-04-13.
         * @param {string} [token] Challenge-response token.
         * @param {string} [blackbox] Examples: SomestringForIOBB
         * @returns {Promise<{playerCode: number, userId: string, actions: Object}>} For actions type see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/types/ActionDataWrapper.html}.
         * @example
         * Playtech.API.auth.register({
         *       dataMap: {
         *           casinoName: 'playtech800075',
         *           clientSkin: 'playtech800075',
         *           title: 'Mr',
         *           firstname: 'test',
         *           lastname: 'test',
         *           sex: 'M',
         *           email: 'test2145@playtech.com',
         *           birthdate: '1986-01-01',
         *           countrycode: 'UA',
         *           state: 'Kyiv',
         *           city: 'Kyiv',
         *           zip: '01001',
         *           cellphone: '+38022112211',
         *           phone: '+380',
         *           address: 'svoboda street',
         *           userName: 'test2145',
         *           password: 'Password1!',
         *           verificationQuestion: 'test',
         *           verificationAnswer: 'test',
         *           currencyCode: 'GBP',
         *           coupon: '',
         *           advertiser: 'advertiser',
         *           clientPlatform: 'web',
         *           signupDeviceType: 'other',
         *           signupOsName: 'macintosh',
         *           signupOsVersion: 'x',
         *           signupDeliveryPlatform: 'Hub2',
         *           signupDeviceBrowser: 'chrome',
         *           language: 'EN',
         *           serial: '460916c7-bd41-42a8-ac82-d85a55924547',
         *           signupDeviceId: '460916c7-bd41-42a8-ac82-d85a55924547',
         *           clientType: 'casino'
         *       },
         *       contactPreferences: {
         *           channel: 'email',
         *           type: 'promotional',
         *           preference: false
         *       },
         *       addressDetails: {
         *           buildingName: 'My Building',
         *           buildingNumber: '1',
         *           subbuilding: '1',
         *           streetName: 'Sportivnaya',
         *           streetNumber: '1',
         *           propertyName: '-',
         *           propertyNumber: '12',
         *           suburb: 'Kyiv'
         *       },
         *       contactAddress: {
         *           city: 'Kyiv',
         *           countryCode: 'UA',
         *           zip: '01012'
         *       }
         * }).then(data => {...});
         */
        register,
        /**
         * Performs login.
         * @memberOf Playtech.API.auth
         * @function
         * @param {string} userName
         * @param {string} password
         * @param {boolean} [rememberMeLogin=false]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.login('test2145', 'Password1!').then(() => {...});
         */
        login,
        /**
         * Shows login popup.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} [onClose] Callback on close popup
         * @param {Function} [onLogin] Callback on login
         * @param {string} [title] Title of popup
         * @example Playtech.API.auth.showLoginPopup({ onClose: (event) => {...}, onLogin: () => {...}, title: 'Login' });
         */
        showLoginPopup,
        /**
         * Show login page or login popup.
         * @memberOf Playtech.API.auth
         * @function
         * @example
         * Playtech.API.auth.showLogin();
         */
        showLogin,
        /**
         * @memberOf Playtech.API.auth
         * @function
         * @param {boolean} [isQuiet]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.logout().then(() => {...});
         */
        logout,
        /**
         * Get temporary authentication token for logged in user.
         * @memberOf Playtech.API.auth
         * @function
         * @param {string} [serviceType=GamePlay]
         * @returns {Promise<string>}
         * @example
         * Playtech.API.auth.getTemporaryAuthenticationToken();
         */
        getTemporaryAuthenticationToken,
        /**
         * @memberOf Playtech.API.auth
         * @returns {boolean}
         * @example
         * Playtech.API.auth.isLoggedIn;
         */
        isLoggedIn,
        /**
         * Add callback after login with credentials.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} callback (resolve, reject) => {...}
         * @example
         * Playtech.API.auth.addAfterCredentialsLoginCallback((resolve, reject) => setTimeout(() => resolve(), 2000));
         */
         addAfterCredentialsLoginCallback,
        /**
         * Add callback to modify the data that is sent to IMS during registration.
         * It must return all arguments that it was given.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} callback (dataMap) => {... return { dataMap }} dataMap from registration form
         * @example
         * Playtech.API.auth.addBeforeRegistrationHook(({ dataMap }) => {
         *     // modify phone right before registration request is sent
         *     dataMap.phone = '+123';
         *
         *     return { dataMap };
         * });
         */
        addBeforeRegistrationHook,
        /**
         * @private
         * This is only for Backbone registration.view.js module.
         */
        getBeforeRegistrationHandler,
         /**
         * Get whether fitToPlaySubmissionRequired is set to '1'.
         * @memberOf Playtech.API.auth
         * @function
         * @returns {boolean}
         * @example
         * Playtech.API.auth.getFitToPlaySubmissionRequired();
         */
        getFitToPlaySubmissionRequired,
        /** Get Two Factor configuration data. [Ims docs]{@link https://integration.playtech.com/apis/openapi/services/IMSConfigurationService/operations/GetTwoFactorConf.html}.
         * @memberOf Playtech.API.auth
         * @function
         * @returns
         * {Promise<{Object}>} {
         *     twoFactorConf: {
         *         enabled: boolean,
         *         loginDeviceSupported: boolean,
         *         rememberMeRequirePin: string,
         *         rememberMeWithoutTwoFactor: ('ON' | 'OFF'),
         *         rememberMeExpirationSec: number,
         *         loginDeviceExpirationSec: number,
         *         defaultChannel: string,
         *         onlyNumbers: boolean,
         *         unblockTokenExpirationSec: number,
         *         configuredServices: { service: string, option: ('ON' | 'OFF') }[],
         *         excludedClientTypePlatforms: { clientType: string, clientPlatform: string }[]
         *     }}
         * @example
         * Playtech.API.auth.getTwoFactorConfiguration().then(data => {...});
         */
        getTwoFactorConfiguration
    }), [
        login,
        logout,
        register,
        showLogin,
        getTemporaryAuthenticationToken,
        addAfterCredentialsLoginCallback,
        getBeforeRegistrationHandler,
        addBeforeRegistrationHook,
        showLoginPopup,
        isLoggedIn,
        getTwoFactorConfiguration
    ]);
};
